import { useCallback, useEffect, useRef, useState } from 'react';
import { z } from 'zod';

import type { ProjectPanelLayout } from '../components/projectPanelLayoutModel';

const STORAGE_KEY_PREFIX = 'vep:project-page:layout:';
const PERSIST_DEBOUNCE_MS = 200;

// The persisted layout segment is the user's explicit 3-way choice (OR-6123
// F4). It is a closed enum, but we keep the stored union open-ended on read
// via the schema fallback so an unknown/legacy value degrades to "no
// preference" rather than throwing.
const PanelSegmentSchema = z.enum(['split', 'ra-only', 'artifacts-only']);

// Mirrors `CHAT_PANEL_MIN_PX` in `ProjectPage.tsx`.
const MIN_CHAT_PIXELS = 428;
const MAX_CHAT_PIXELS = 4000;

const StoredLayoutSchema = z.object({
  // Pairs with the chat panel's `groupResizeBehavior="preserve-pixel-size"`
  // so a restored width round-trips exactly instead of being re-derived at mount.
  chatPixels: z.number().min(MIN_CHAT_PIXELS).max(MAX_CHAT_PIXELS).optional(),
  // Explicit 3-way layout segment. Optional so a width-only write (legacy
  // shape, drag before any toggle) is still valid.
  segment: PanelSegmentSchema.optional(),
});

export type StoredProjectPanelLayout = z.infer<typeof StoredLayoutSchema>;

/**
 * First-load chat panel width. Matches the legacy `GLOBAL_RA_WIDTH`
 * (428px) so the agent-first chat opens at roughly the same width users
 * are accustomed to from the pre-gen-3 global Research Assistant. After
 * the first drag, the user's preferred pixel width is persisted and
 * takes over.
 */
const DEFAULT_CHAT_PIXELS = '428px';

const storageKey = (projectId: string) => `${STORAGE_KEY_PREFIX}${projectId}`;

const readLayout = (projectId: string): StoredProjectPanelLayout | null => {
  try {
    const raw = window.localStorage.getItem(storageKey(projectId));
    if (!raw) return null;
    const parsed = StoredLayoutSchema.safeParse(JSON.parse(raw));
    if (!parsed.success) return null;
    return parsed.data;
  } catch {
    return null;
  }
};

const writeLayout = (projectId: string, layout: StoredProjectPanelLayout) => {
  try {
    window.localStorage.setItem(storageKey(projectId), JSON.stringify(layout));
  } catch {
    // localStorage unavailable (private mode, quota) — silent best-effort
  }
};

const clampPixels = (pixels: number): number =>
  Math.min(MAX_CHAT_PIXELS, Math.max(MIN_CHAT_PIXELS, Math.round(pixels)));

export interface UseProjectPanelLayoutResult {
  /**
   * Chat panel width as a pixel string, for `Panel`'s `defaultSize`.
   * Read fresh off the ref each call, not a one-time mount snapshot.
   */
  initialChatSize: string;
  /**
   * The persisted 3-way layout segment for this project, or `null` when the
   * user has never explicitly chosen one. The page seeds its visibility
   * levers from this on mount (OR-6123 F4). Captured once per `projectId`
   * change — a later `persistLayout` does NOT mutate it (it is a restore
   * seed, not live state).
   */
  initialSegment: ProjectPanelLayout | null;
  /** Persist a new chat pixel width without triggering a React re-render. */
  persistChatPixels: (pixels: number) => void;
  /**
   * Persist the user's explicit 3-way layout choice (OR-6123 F4).
   *
   * Call this **only** from the explicit toggle/chevron handlers — never
   * from an effect keyed on the *derived* layout. The derived layout flips
   * to `artifacts-only` whenever the route-availability matcher rejects the
   * route (e.g. the cohort-builder Results tab); persisting that derived
   * value would silently clobber the user's real preference. Persist on
   * intent, mirroring the `lastActiveAt`-on-interaction rule (plan D1).
   */
  persistLayout: (segment: ProjectPanelLayout) => void;
}

/**
 * Persists per-project chat-panel width **and** the 3-way layout segment
 * (split / RA-only / artifacts-only) so the panel configuration survives
 * reload and navigation (OR-6123 F4).
 *
 * Width is _not_ React state during drag — every `onResize` tick would
 * otherwise re-render the whole project page. Persisted writes are
 * debounced; `initialChatSize` reads straight off the ref instead.
 *
 * The segment is surfaced as `initialSegment` (a one-time restore seed); the
 * page maps it onto its visibility levers (RA store open/close + local
 * content collapse) on mount. Live segment changes flow through
 * `persistLayout` on explicit user intent only.
 */
export const useProjectPanelLayout = (projectId: string): UseProjectPanelLayoutResult => {
  // Captured once per `projectId` change. Drag updates do NOT mutate this.
  const [initialLayout, setInitialLayout] = useState<StoredProjectPanelLayout | null>(() =>
    readLayout(projectId)
  );

  // Ref so persist closures and `initialChatSize` reads stay current
  // without re-rendering on every drag tick.
  const layoutRef = useRef<StoredProjectPanelLayout>(initialLayout ?? {});

  // Re-read on project change (navigation between projects).
  useEffect(() => {
    const fresh = readLayout(projectId);
    setInitialLayout(fresh);
    layoutRef.current = fresh ?? {};
  }, [projectId]);

  // Debounced persist — drag-resize fires onResize many times per second,
  // and we don't want to JSON.stringify + write every step.
  const writeTimerRef = useRef<number | null>(null);
  const schedulePersist = useCallback(
    (next: StoredProjectPanelLayout) => {
      layoutRef.current = next;
      if (writeTimerRef.current !== null) {
        window.clearTimeout(writeTimerRef.current);
      }
      writeTimerRef.current = window.setTimeout(() => {
        writeLayout(projectId, layoutRef.current);
        writeTimerRef.current = null;
      }, PERSIST_DEBOUNCE_MS);
    },
    [projectId]
  );

  // Flush pending writes on unmount so we don't lose the last drag step.
  useEffect(() => {
    return () => {
      if (writeTimerRef.current !== null) {
        window.clearTimeout(writeTimerRef.current);
        writeLayout(projectId, layoutRef.current);
      }
    };
  }, [projectId]);

  const persistChatPixels = useCallback(
    (pixels: number) => {
      const clamped = clampPixels(pixels);
      if (layoutRef.current.chatPixels === clamped) return;
      schedulePersist({ ...layoutRef.current, chatPixels: clamped });
    },
    [schedulePersist]
  );

  const persistLayout = useCallback(
    (segment: ProjectPanelLayout) => {
      if (layoutRef.current.segment === segment) return;
      schedulePersist({ ...layoutRef.current, segment });
    },
    [schedulePersist]
  );

  return {
    initialChatSize:
      layoutRef.current.chatPixels != null
        ? `${layoutRef.current.chatPixels}px`
        : DEFAULT_CHAT_PIXELS,
    initialSegment: initialLayout?.segment ?? null,
    persistChatPixels,
    persistLayout,
  };
};
